/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { Card, Row, Col, message, Spin, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  DropDownComponent,
  TableComponent,
  CalendarComponent,
  RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadBookingStatusReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";

const BookingStatusReport = () => {
  // Set default date range to last 7 days
  const defaultDateRange = [
    moment().subtract(1, "days"),
    moment()
  ];

  const [loading, setLoading] = useState(false);
  const [hubListLoading, setHubListLoading] = useState(false);
  const [cpListLoading, setCpListLoading] = useState(false);
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [hubList, setHubList] = useState([]);
  const [selectedHubs, setSelectedHubs] = useState([]);
  const [cpList, setCpList] = useState([]);
  const [selectedCps, setSelectedCps] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  });
  const [reportData, setReportData] = useState([]);
  const [hubUser, setHubUser] = useState();
  const [lastsynced, setLastsynced] = useState(null);
  const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
  // Request cancellation hook
  const { createNewRequest, isLatestRequest } = useRequestCancellation();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userinfo"));
    let userid = JSON.parse(localStorage.getItem("userId"));
    setHubUser(user?.roles?.[0]);

    const activeHub = JSON.parse(localStorage.getItem("active_hub"));
    const activeHubId = activeHub?.hubId;

    if (hubList.length > 0) {
      if (activeHubId) {
        const defaultHub = hubList.find((hub) => String(hub.value) === String(activeHubId));
        if (defaultHub) {
          setSelectedHubs([defaultHub.value]);
        }
      } else if (user?.roles?.[0] === "ROLE_HUBADMIN") {
        const defaultHub = hubList.find(
          (hub) => String(hub.value) === String(userid?.userInfo?.premiseId)
        );
        if (defaultHub) {
          setSelectedHubs([defaultHub.value]);
        }
      }
    }
  }, [hubList]);

  // Fetch hub list on mount
  useEffect(() => {
    (async () => {
      try {
        setHubListLoading(true);
        const hubs = await getHubList();
        setHubList(hubs);
      } catch {
        message.error("Failed to fetch hub list");
      } finally {
        setHubListLoading(false);
      }
    })();
  }, []);

  // Fetch CP list when hubs change
  useEffect(() => {
    if (selectedHubs.length > 0) {
      setCpListLoading(true);
      Promise.all(selectedHubs.map(hubId => getHubCPList(hubId)))
        .then(cpLists => {
          const allCps = [...new Set(cpLists.flat())];
          setCpList(allCps);
        })
        .catch(() => {
          message.error("Failed to fetch CP lists");
          setCpList([]);
        })
        .finally(() => {
          setCpListLoading(false);
        });
    } else {
      setCpList([]);
      setCpListLoading(false);
    }
  }, [selectedHubs]);

  // Fetch report data when filters or pagination change
  useEffect(() => {
    // Wait for hubUser to be initialized
    if (!hubUser) {
      return;
    }

    // For HUBADMIN users, hub selection is mandatory
    if (hubUser === "ROLE_HUBADMIN" && selectedHubs.length === 0) {
      return; // Don't fetch if HUBADMIN has no hub selected
    }

    fetchReport();
    // eslint-disable-next-line
  }, [hubUser, selectedHubs, selectedCps, dateRange, pagination.current, pagination.pageSize]);

  const fetchReport = async () => {
    // Create new request with cancellation support
    const { signal, requestId } = createNewRequest("booking-status-report");

    setLoading(true);
    try {
      // For HUBADMIN users, hub selection is mandatory
      if (hubUser === "ROLE_HUBADMIN" && selectedHubs.length === 0) {
        setReportData([]);
        setPagination(prev => ({
          ...prev,
          total: 0
        }));
        return;
      }

      const selectedHubData = hubList.filter(hub => selectedHubs?.includes(hub.value));
      const selectedCpData = cpList.filter(cp => selectedCps?.includes(cp.value));

      // Prepare params according to API requirements
      const params = {
        ...(selectedHubData?.length > 0 && { hubNames: selectedHubData.map(hub => hub.label) }),
        ...(selectedCpData?.length > 0 && { cpNames: selectedCpData.map(cp => cp.label) }),
        ...(dateRange[0] && dateRange[1] && {
          startDate: dateRange[0].format("YYYY-MM-DD"),
          endDate: dateRange[1].format("YYYY-MM-DD")
        }),
        page: pagination.current,
        pageSize: pagination.pageSize
      };

      const { data, total, page, pageSize, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getBookingStatusReport(params, { signal });

      // Check if this is still the latest request
      if (!isLatestRequest(requestId)) {
        console.log("🔥 Request cancelled - ignoring response");
        return;
      }

      setReportData(data);
      setLastsynced(lastRefreshedAt);
      setNextScheduledRefresh(nextScheduledRefresh);
      setPagination(prev => ({
        ...prev,
        total,
        current: page,
        pageSize
      }));
    } catch (error) {
      // Check if this is still the latest request
      if (!isLatestRequest(requestId)) {
        console.log("🔥 Request cancelled - ignoring error");
        return;
      }

      if (error.name === "AbortError") {
        console.log("🔥 Request was cancelled");
        return;
      }

      console.error("Error fetching report data:", error);
      message.error("Failed to fetch report data");
      setReportData([]);
      setPagination(prev => ({
        ...prev,
        total: 0
      }));
    } finally {
      // Only update loading state if this is still the latest request
      if (isLatestRequest(requestId)) {
        setLoading(false);
      }
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleDownload = async () => {
    try {
      // Validate required fields
      if (!dateRange[0] || !dateRange[1]) {
        message.error("Please select date range for download");
        return;
      }

      /*if (selectedHubs.length === 0) {
        message.error("Hub selection is required");
        return;
      }*/

      const selectedHubData = hubList.filter(hub => selectedHubs?.includes(hub.value));
      const selectedCpData = cpList.filter(cp => selectedCps?.includes(cp.value));

      const downloadParams = {
        startDate: dateRange[0].format("YYYY-MM-DD"),
        endDate: dateRange[1].format("YYYY-MM-DD"),
        fromHubs: selectedHubData.length > 0 ? selectedHubData.map(hub => hub.label) : undefined,
        fromCPs: selectedCpData.length > 0 ? selectedCpData.map(cp => cp.label) : undefined
      };

      await downloadBookingStatusReport(downloadParams);
      message.success("Download started successfully");
    } catch (error) {
      console.error("Download error:", error);
      message.error("Failed to start download");
    }
  };

  // Table columns (customize as needed)
  const columns = [
    {
      title: "No.",
      dataIndex: "index",
      key: "index",
      sorter: true,
      width: 70,
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Shipment No", dataIndex: "shipmentNo", key: "shipmentNo", sorter: true, width: 140,
      render: (text) => (
        <span
          style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
          onClick={() => {
            const url = `/shipment-tracker?awb=${text}`;
            window.open(url, "_blank");
          }}
        >
          {text}
        </span>
      )
    },
    { title: "Booking Datetime", dataIndex: "bookingDatetime", key: "bookingDatetime", sorter: true, width: 140 },
    { title: "From Center", dataIndex: "fromCenter", key: "fromCenter", sorter: true, width: 120 },
    { title: "From Hub", dataIndex: "fromHub", key: "fromHub", sorter: true, width: 160 },
    { title: "From CP", dataIndex: "fromCP", key: "fromCP", sorter: true, width: 160 },
    { title: "Current Hub", dataIndex: "currentPremiseName", key: "currentPremiseName", sorter: true, width: 160 },
    { title: "Current Status", dataIndex: "currentStatus", key: "currentStatus", sorter: true, width: 120 },
    { title: "Status", dataIndex: "status", key: "status", sorter: true, width: 120 },
    { title: "From Pincode", dataIndex: "fromPincode", key: "fromPincode", sorter: true, width: 120 },
    { title: "Audited Weight (kg)", dataIndex: "auditedWeight", key: "auditedWeight", sorter: true, width: 120 },
    { title: "Product Type", dataIndex: "productType", key: "productType", sorter: true, width: 120 },
    { title: "Booking Type", dataIndex: "bookingType", key: "bookingType", sorter: true, width: 120 },
    { title: "Transport Mode", dataIndex: "transportMode", key: "transportMode", sorter: true, width: 120 },
    { title: "To Hub", dataIndex: "toHub", key: "toHub", sorter: true, width: 160 },
    { title: "To Center", dataIndex: "toCenter", key: "toCenter", sorter: true, width: 120 },
    { title: "To Pincode", dataIndex: "toPincode", key: "toPincode", sorter: true, width: 120 },
  ];

  return (
    <div className="report-container">
      <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
        <Heading title="Booking Data vs Status Report">
          <div style={{ paddingTop: "5px" }}>
            <RefreshTimestamp
              lastRefreshedAt={lastsynced}
              nextScheduledRefresh={nextScheduledRefresh}
            />
          </div>
        </Heading>
      </div>
      <Card className="report-card">
        <Row gutter={[16, 16]} className="filters-section">
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Date Range</div>
            <CalendarComponent
              type="range"
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">From Hub</div>
            <DropDownComponent
              className="small-dropdown"
              placeholder="From Hub"
              options={hubList}
              onChange={setSelectedHubs}
              value={selectedHubs}
              allowClear
              mode={hubUser !== "ROLE_HUBADMIN" ? "multiple" : "default"}
              disabled={hubUser === "ROLE_HUBADMIN"}
              loading={hubListLoading}
              showSearch={true}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">From CP</div>
            <DropDownComponent
              className="small-dropdown"
              placeholder="From CP"
              options={cpList}
              onChange={setSelectedCps}
              value={selectedCps}
              allowClear
              mode="multiple"
              maxTagCount="responsive"
              disabled={selectedHubs.length === 0}
              loading={cpListLoading}
              showSearch={true}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">&nbsp;</div>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              loading={loading}
              disabled={!dateRange[0] || !dateRange[1]}
              style={{ width: "100%" }}
            >
              Download Report
            </Button>
          </Col>
        </Row>
      </Card>
      <Spin spinning={loading}>
        <TableComponent
          columns={columns}
          dataSource={reportData}
          loading={false} // Set to false since we're using Spin wrapper
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
          sticky
        />
      </Spin>
    </div>
  );
};

export default BookingStatusReport; 
